/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormHelperText,
  IconButton,
  Radio,
  Select,
  Typography,
} from "@mui/material";
import PhoneInput from "react-phone-input-2";
import { fonts } from "../../../themes/dashboard";
import { StyledAddButton, StyledFormFieldText } from "../styles";

export const CLOSE_ICON_COLOR = "#707070";
export const REQUIRED_FIELD_ASTERISK_COLOR = "#ff0000";
const PARTNER_CARD_ACCENT_COLOR = "#EA580C";

export { StyledTextField } from "../styles";

export const PartnershipSubmitButton = styled(StyledAddButton)({
  "&.Mui-disabled": { 
    backgroundColor: "#e0e0e0",
  },
  "&:hover": {
    backgroundColor: "#395bbd",
    boxShadow: "none",
  },
  backgroundColor: "#395bbd",
  boxShadow: "none",
  borderRadius: "8px",
  fontSize: "13px",
  fontWeight: "600",
  height: "35px",
  minWidth: "120px",
  textTransform: "none",
});

export const PartnershipDialog = styled(Dialog)({
  "& .MuiBackdrop-root": {
    backgroundColor: "rgba(15, 23, 42, 0.45)",
  },
  "& .MuiDialog-paper": {
    width: "min(640px, calc(100vw - 32px))",
    maxWidth: "640px",
    borderRadius: "12px",
    backgroundImage: "none",
  },
});

export const PartnershipDialogHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
  padding: "16px 20px 0px",
});

export const PartnershipDialogTitle = styled(Typography)({
  fontSize: "20px",
  fontWeight: 700,
  lineHeight: 1.2,
  color: "rgba(81, 81, 82, 1)",
});

export const SubtitleText = styled(Typography)({
  fontSize: "13px",
  color: "#6b7280",
  margin: "0px 0px 10px 0px",
});

export const PartnershipDialogContent = styled(DialogContent)({
  padding: "8px 20px",
});

export const FormContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

export const FieldStack = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

export const TwoColumnGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "16px",
  alignItems: "start",
  "@media (max-width: 600px)": {
    gridTemplateColumns: "1fr",
  },
});

export const CustomStyledFormFieldText = styled(StyledFormFieldText)({
  marginBottom: "3px",
  fontSize: "14px !important",
});

export const StyledSelect = styled(Select)({
  width: "100%",
  borderRadius: "8px",
  "& .MuiInputBase-root": {
    minHeight: "40px",
  },
  "& .MuiSelect-select": {
    paddingTop: "8.5px",
    paddingBottom: "8.5px",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#D8D9DB",
    borderRadius: "8px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#395bbd",
    borderWidth: "2px",
  },
});

export const HelperError = styled(FormHelperText)({
  marginLeft: 0,
  color: "#d32f2f",
});

export const PairLabelText = styled(Typography)({
  fontSize: "16px",
  fontWeight: 600,
  color: "rgba(81, 81, 82, 1)",
});

export const StyledDialogActions = styled(DialogActions)({
  padding: "12px 20px 20px",
  gap: "8px",
});

/* Level-1 partner category tabs (Restaurant Group Partner / Business Partner) */

export const PartnerCategoryGroup = styled(Box)({
  backgroundColor: "#f4f4f5",
  borderRadius: "8px",
  display: "flex",
  gap: "2px",
  padding: "3px",
  width: "100%",
});

export const PartnerCategoryOption = styled(Button, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})(({ isSelected }) => ({
  backgroundColor: isSelected ? "#ffffff" : "transparent",
  borderRadius: "6px",
  boxShadow: isSelected ? "0px 1px 2px rgba(0, 0, 0, 0.08)" : "none",
  color: isSelected ? "#18181b" : "#8b8b93",
  flex: 1,
  fontFamily: fonts?.fontFamily,
  fontSize: "13px",
  fontWeight: "600",
  letterSpacing: "0px",
  padding: "6px 18px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: isSelected ? "#ffffff" : "#ebebed",
    boxShadow: isSelected ? "0px 1px 2px rgba(0, 0, 0, 0.08)" : "none",
  },
}));

/* Level-2 business partner source cards (Existing partner / New partner) */

export const PartnerCardGroup = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "12px",
  "@media (max-width: 600px)": {
    gridTemplateColumns: "1fr",
  },
});

// Mirrors MatchCard in components/merge_modal/styles.jsx.
export const PartnerCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})(({ isSelected }) => ({
  alignItems: "flex-start",
  backgroundColor: isSelected ? "#faf3f1" : "#f7f7f7",
  border: isSelected
    ? `1px solid ${PARTNER_CARD_ACCENT_COLOR}`
    : "1px solid rgba(224, 224, 224, 1)",
  borderRadius: "8px",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  padding: "12px",
}));

export const PartnerCardHeader = styled(Box)({
  alignItems: "center",
  display: "flex",
  gap: "8px",
});

// Carries the keyboard affordance for the cards, which are otherwise plain boxes.
export const PartnerCardRadio = styled(Radio)({
  padding: "0px",
  color: "#71717a",
  "&.Mui-checked": { color: PARTNER_CARD_ACCENT_COLOR },
  "& .MuiSvgIcon-root": { fontSize: "18px" },
});

export const PartnerCardIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})(({ isSelected }) => ({
  alignItems: "center",
  color: isSelected ? PARTNER_CARD_ACCENT_COLOR : "#71717a",
  display: "flex",
  "& .MuiSvgIcon-root": { fontSize: "20px" },
}));

export const PartnerCardTitle = styled(Typography)({
  color: "#18181b",
  fontFamily: fonts?.fontFamily,
  fontSize: "14px",
  fontWeight: "600",
});

export const PartnerCardDescription = styled(Typography)({
  color: "#71717a",
  fontFamily: fonts?.fontFamily,
  fontSize: "12px",
  fontWeight: "400",
  lineHeight: 1.4,
});

export const PartnerSectionDivider = styled(Box)({
  borderTop: "1px solid #e4e4e7",
  margin: "4px 0px",
});

/* Shared clear ("x") icon button — used both as an absolutely-positioned overlay inside a
   clearable select, and as a plain end adornment on the custom-partner-type text field. */
export const InlineClearIconButton = styled(IconButton)({
  padding: "2px",
  color: "#8b8b93",
  "&:hover": { color: "#18181b", backgroundColor: "transparent" },
  "& .MuiSvgIcon-root": { fontSize: "16px" },
});

export const SelectClearButton = styled(InlineClearIconButton)({
  position: "absolute",
  right: "32px",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 1,
});

// Sized to ~40px to match this modal's other fields, unlike the 50px variant used in
// restaurant/create/basic_information.
export const StyledPhoneInput = styled(PhoneInput)({
  "&:hover": {
    outline: "2px solid #395bbd !important",
    outlineOffset: "-1px",
  },
  "&:focus-within": {
    outline: "2px solid #1976d2 !important",
    outlineOffset: "-1px",
  },
  height: "40px",
  borderRadius: "8px",
  borderWidth: "0.25px",
  gap: "4px",
});
